import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import useI18n from 'hooks/useI18n'
import {
  ANALYTICS_SECTIONS,
  ReadAnalyticsAccess,
  ReadAnalyticsFilters,
  ReadAnalyticsSection,
} from 'services/analytics-dashboard'
import { trackEvent } from 'services/analytics'
import { apiRequest, exposeStrapiError } from 'utils'
import { languages } from 'utils/options'

export const ANALYTICS_PERIODS = ['today', '7d', '30d', '90d', 'year', 'custom']

const FILTER_KEYS = [
  'period',
  'from',
  'to',
  'persona',
  'course',
  'category',
  'program',
  'trainer',
  'survey',
  'area',
  'competitiveSection',
  'competitor',
  'source',
  'assistant',
  'search',
  'event',
  'user',
  'content',
  'page',
  'pageSize',
  'sort',
  'direction',
]

const responseStatus = result => Number(result?.error?.status || result?.status || 0)
const unwrap = result => result?.data?.data || result?.data || result || null

export const parseAnalyticsLocation = search => {
  const query = new URLSearchParams(search || '')
  const section = query.get('section')
  const period = query.get('period')
  const filters = {}

  FILTER_KEYS.forEach(key => {
    const value = query.get(key)
    if (value !== null && value !== '') filters[key] = value
  })

  const resolvedSection = ANALYTICS_SECTIONS.includes(section) ? section : 'overview'
  return {
    section: resolvedSection === 'invitations' && filters.area === 'universe' ? 'overview' : resolvedSection,
    filters: {
      ...filters,
      period: ANALYTICS_PERIODS.includes(period) ? period : '30d',
      page: Math.max(1, Number(filters.page || 1)),
      pageSize: Math.min(100, Math.max(10, Number(filters.pageSize || 25))),
    },
  }
}

export const buildAnalyticsSearch = (search, patch, { resetPage = true } = {}) => {
  const query = new URLSearchParams(search || '')

  Object.entries(patch).forEach(([key, value]) => {
    if (value === undefined || value === null || `${value}` === '') query.delete(key)
    else query.set(key, value)
  })

  if (patch.period && patch.period !== 'custom') {
    query.delete('from')
    query.delete('to')
  }
  if (resetPage && !Object.prototype.hasOwnProperty.call(patch, 'page')) query.delete('page')

  return query.toString() ? `?${query}` : ''
}

export const buildAnalyticsResetSearch = section => {
  const query = new URLSearchParams()
  if (section && section !== 'overview') query.set('section', section)
  return query.toString() ? `?${query}` : ''
}

const containsMeasuredData = (section, data) => {
  if (!data) return false
  if (section === 'competitive') return (
    Object.values(data.kpis || {}).some(value => Number(value) > 0)
    || (data.contentRows || []).length > 0
    || Object.values(data.operational?.totals || {}).some(value => Number(value) > 0)
  )
  if (section === 'activity') return (
    (Array.isArray(data.activityBreakdown) && data.activityBreakdown.length > 0)
    || (Array.isArray(data.items) && data.items.length > 0)
  )
  if (section === 'invitations') return (
    Object.values(data.kpis || {}).some(value => Number(value) > 0)
    || (data.performance || []).length > 0
  )

  const hasKpi = [data.kpis, data.onboarding?.kpis, data.certificates?.kpis]
    .some(group => Object.values(group || {}).some(value => Number(value) > 0))
  const hasList = [
    'timeline',
    'courseRows',
    'videoRows',
    'fileRows',
    'funnel',
    'noWatchUsers',
    'activeUsersOverTime',
  ].some(key => Array.isArray(data[key]) && data[key].length > 0)

  return hasKpi || hasList
}

const normalizeOptions = values => (values || []).map(value => {
  if (typeof value === 'string' || typeof value === 'number') {
    return { id: `${value}`, title: `${value}` }
  }

  return {
    ...value,
    id: `${value?.id ?? value?.value ?? value?.key ?? ''}`,
    title: value?.title || value?.label || value?.name || `${value?.id ?? ''}`,
  }
}).filter(option => option.id)

export default function useController() {
  const history = useHistory()
  const location = useLocation()
  const { language, t } = useI18n()
  const requestId = useRef(0)
  const [accessStatus, setAccessStatus] = useState('loading')
  const [filterOptions, setFilterOptions] = useState({})
  const [state, setState] = useState({ status: 'loading', data: null })
  const { filters, section } = useMemo(
    () => parseAnalyticsLocation(location.search),
    [location.search]
  )
  const locale = useMemo(
    () => languages.find(item => item.id === language)?.ref || 'en',
    [language]
  )

  const updateQuery = useCallback((patch, { replace = false, resetPage = true } = {}) => {
    const nextLocation = {
      pathname: location.pathname,
      search: buildAnalyticsSearch(location.search, patch, { resetPage }),
    }
    if (replace) history.replace(nextLocation)
    else history.push(nextLocation)
  }, [history, location.pathname, location.search])

  const selectSection = useCallback(nextSection => {
    const surveyDates = nextSection === 'surveys' && (!filters.from || !filters.to)
      ? (() => {
        const to = new Date()
        const from = new Date(to)
        from.setDate(from.getDate() - 29)
        return { period: 'custom', from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
      })()
      : {}
    updateQuery({ section: nextSection === 'overview' ? null : nextSection, ...surveyDates })
    trackEvent('analytics_section_view', { section: nextSection })
  }, [filters.from, filters.to, updateQuery])

  const setFilter = useCallback((key, value) => {
    if (key === 'period' && value === 'custom' && (!filters.from || !filters.to)) {
      const to = new Date()
      const from = new Date(to)
      from.setDate(from.getDate() - 29)
      updateQuery({
        period: 'custom',
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      })
      return
    }
    updateQuery({
      [key]: value,
      ...(key === 'area' && value === 'universe' && section === 'invitations' ? { section: null } : {}),
    })
  }, [filters.from, filters.to, section, updateQuery])

  const setPage = useCallback(page => {
    updateQuery({ page }, { resetPage: false })
  }, [updateQuery])

  const setSort = useCallback(key => {
    updateQuery({
      sort: key,
      direction: filters.sort === key && filters.direction === 'desc' ? 'asc' : 'desc',
    })
  }, [filters.direction, filters.sort, updateQuery])

  const resetFilters = useCallback(() => {
    history.push({ pathname: location.pathname, search: buildAnalyticsResetSearch(section) })
    trackEvent('analytics_filters_reset', { section })
  }, [history, location.pathname, section])

  const loadAccess = useCallback(async () => {
    setAccessStatus('loading')
    const result = await apiRequest(ReadAnalyticsAccess)
    const status = responseStatus(result)
    const access = unwrap(result)

    if (!result || result?.error || access?.canRead !== true) {
      if (result?.error && ![401, 403].includes(status)) exposeStrapiError(result)
      setAccessStatus([401, 403].includes(status) || access?.canRead === false ? 'denied' : 'error')
      return
    }

    setAccessStatus('ready')
  }, [])

  useEffect(() => {
    loadAccess()
  }, [loadAccess])

  useEffect(() => {
    if (accessStatus !== 'ready') return undefined
    let active = true

    apiRequest(() => ReadAnalyticsFilters({ locale }))
      .then(result => {
        if (!active || !result || result?.error) return
        const data = unwrap(result)
        setFilterOptions({
          personas: normalizeOptions(data?.personas),
          courses: normalizeOptions(data?.courses),
          categories: normalizeOptions(data?.categories),
          programs: normalizeOptions(data?.programs),
          trainers: normalizeOptions(data?.trainers),
          surveys: normalizeOptions(data?.surveys),
          areas: normalizeOptions(data?.areas),
          events: normalizeOptions(data?.events),
          competitiveSections: normalizeOptions(data?.competitiveSections),
          competitors: normalizeOptions(data?.competitors),
          competitiveSources: normalizeOptions(data?.competitiveSources),
          assistants: normalizeOptions(data?.assistants),
        })
      })

    return () => { active = false }
  }, [accessStatus, locale])

  const loadSection = useCallback(async (requestedSection = section, requestedFilters = filters) => {
    const currentRequest = ++requestId.current
    setState(current => ({ ...current, status: 'loading' }))
    const requestedSections = requestedSection === 'overview'
      ? ['overview', 'onboarding', 'certificates']
      : requestedSection === 'activity'
        ? ['activity', 'overview']
        : [requestedSection]
    const results = await Promise.all(requestedSections.map(item => (
      apiRequest(() => ReadAnalyticsSection(item, requestedFilters))
    )))
    if (currentRequest !== requestId.current) return null

    const failedResult = results.find(result => !result || result?.error)
    if (failedResult) {
      const status = responseStatus(failedResult)
      if (failedResult?.error && ![401, 403].includes(status)) exposeStrapiError(failedResult)
      setState({ status: [401, 403].includes(status) ? 'denied' : 'error', data: null })
      return null
    }

    const payloads = requestedSections.map((item, index) => [item, unwrap(results[index])])
    const data = requestedSection === 'overview'
      ? {
        ...payloads[0][1],
        onboarding: payloads.find(([item]) => item === 'onboarding')?.[1],
        certificates: payloads.find(([item]) => item === 'certificates')?.[1],
      }
      : requestedSection === 'activity'
        ? {
          ...payloads[0][1],
          activityOverview: payloads.find(([item]) => item === 'overview')?.[1],
        }
        : payloads[0][1]
    const empty = !containsMeasuredData(requestedSection, data)
    setState({ status: empty ? 'empty' : 'ready', data })
    trackEvent('analytics_section_loaded', {
      section: requestedSection,
      period: requestedFilters.period,
      empty,
    })
    return data
  }, [filters, section])

  const loadReportSection = useCallback(async requestedSection => {
    const result = await apiRequest(() => ReadAnalyticsSection(requestedSection, filters))
    if (!result || result?.error) throw new Error(`Could not load ${requestedSection} analytics`)
    return unwrap(result)
  }, [filters])

  useEffect(() => {
    if (accessStatus === 'ready') loadSection()
  }, [accessStatus, loadSection])

  const formatters = useMemo(() => ({
    number: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    percent: new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }),
    date: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    dateTime: new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
  }), [locale])

  const parseDate = useCallback(value => {
    if (!value) return null
    const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(`${value}`) ? `${value}T00:00:00` : value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }, [])

  return {
    ...state,
    accessStatus,
    filterOptions,
    filters,
    language,
    loadSection,
    loadReportSection,
    locale,
    resetFilters,
    retry: accessStatus === 'error' ? loadAccess : loadSection,
    section,
    sections: filters.area === 'universe'
      ? ANALYTICS_SECTIONS.filter(item => item !== 'invitations')
      : ANALYTICS_SECTIONS,
    selectSection,
    setFilter,
    setPage,
    setSort,
    t,
    formatNumber: value => formatters.number.format(Number(value || 0)),
    formatPercent: value => formatters.percent.format(Number(value || 0) / 100),
    formatDuration: value => {
      const seconds = Math.max(0, Math.round(Number(value || 0)))
      return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`
    },
    formatDate: value => parseDate(value) ? formatters.date.format(parseDate(value)) : '—',
    formatDateTime: value => parseDate(value) ? formatters.dateTime.format(parseDate(value)) : '—',
  }
}
